<template>
    <portal to="drawer">
        <transition name="drawer" @enter="onEnter" @leave="onLeave" :css="false">
            <div v-if="isVisible" class="mobile-drawer-overlay" @click="handleOverlayClick">
                <div class="mobile-drawer" @click.stop ref="drawer">
                    <div class="mobile-drawer-header" ref="header">
                        <div class="mobile-drawer-handle"></div>
                        <h3>{{ title }}</h3>
                        <button class="mobile-drawer-close" @click="close" :aria-label="closeLabel">
                            <i class="ri-close-line"></i>
                        </button>
                    </div>
                    <div
                        class="mobile-drawer-content"
                        ref="content"
                        :class="{ 'toolbar-variant': variant === 'toolbar' }"
                    >
                        <slot></slot>
                    </div>
                </div>
            </div>
        </transition>
    </portal>
</template>

<script>
import { gsap } from 'gsap';

export default {
    name: 'MobileDrawer',
    props: {
        isVisible: {
            type: Boolean,
            default: false,
        },
        title: {
            type: String,
            default: '',
        },
        closeLabel: {
            type: String,
            default: 'Schließen',
        },
        closeOnOverlay: {
            type: Boolean,
            default: true,
        },
        swipeToClose: {
            type: Boolean,
            default: true,
        },
        maxHeight: {
            type: String,
            default: '85vh',
        },
        variant: {
            type: String,
            default: 'default',
            validator: (value) => ['default', 'toolbar'].includes(value),
        },
    },
    data() {
        return {
            isDragging: false,
            startY: 0,
            currentY: 0,
        };
    },
    watch: {
        isVisible(newVal) {
            if (newVal) {
                this.$nextTick(() => {
                    this.setupSwipeGestures();
                });
            }
        },
    },
    methods: {
        close() {
            this.$emit('close');
        },
        handleOverlayClick() {
            if (this.closeOnOverlay) {
                this.close();
            }
        },
        onEnter(el, done) {
            this.animateDrawerIn(done);
        },
        onLeave(el, done) {
            this.animateDrawerOut(done);
        },
        animateDrawerIn(done) {
            this.$nextTick(() => {
                const drawer = this.$refs.drawer;
                const overlay = drawer?.parentElement;

                if (drawer && overlay) {
                    gsap.set(drawer, {
                        y: '100%',
                        force3D: true,
                        transformOrigin: 'bottom center',
                    });
                    gsap.set(overlay, { opacity: 0 });

                    const timeline = gsap.timeline({
                        onComplete: () => {
                            this.$emit('opened');
                            if (done) done();
                        },
                    });

                    timeline.to(
                        overlay,
                        {
                            opacity: 1,
                            duration: 0.25,
                            ease: 'power2.out',
                        },
                        0,
                    );

                    timeline.to(
                        drawer,
                        {
                            y: '0%',
                            duration: 0.35,
                            ease: 'power2.out',
                            force3D: true,
                        },
                        0.05,
                    );
                }
            });
        },
        animateDrawerOut(done) {
            const drawer = this.$refs.drawer;
            const overlay = drawer?.parentElement;

            if (drawer && overlay) {
                const timeline = gsap.timeline({
                    onComplete: () => {
                        this.$emit('closed');
                        if (done) done();
                    },
                });

                timeline.to(
                    drawer,
                    {
                        y: '100%',
                        duration: 0.3,
                        ease: 'power2.in',
                        force3D: true,
                    },
                    0,
                );

                timeline.to(
                    overlay,
                    {
                        opacity: 0,
                        duration: 0.2,
                        ease: 'power2.in',
                    },
                    0.05,
                );
            }
        },
        setupSwipeGestures() {
            if (!this.swipeToClose) return;

            const drawer = this.$refs.drawer;
            const header = this.$refs.header;

            if (!drawer || !header) return;

            const handleTouchStart = (e) => {
                this.startY = e.touches[0].clientY;
                this.isDragging = true;
                this.currentY = this.startY;
            };

            const handleTouchMove = (e) => {
                if (!this.isDragging) return;

                this.currentY = e.touches[0].clientY;
                const deltaY = this.currentY - this.startY;

                if (deltaY > 0) {
                    const translateY = Math.min(deltaY, 100);
                    gsap.set(drawer, { y: `${translateY}px`, force3D: true });
                }
            };

            const handleTouchEnd = () => {
                if (!this.isDragging) return;

                const deltaY = this.currentY - this.startY;

                if (deltaY > 50) {
                    this.close();
                } else {
                    gsap.to(drawer, {
                        y: '0%',
                        duration: 0.2,
                        ease: 'power2.out',
                        force3D: true,
                    });
                }

                this.isDragging = false;
            };

            header.addEventListener('touchstart', handleTouchStart, { passive: true });
            header.addEventListener('touchmove', handleTouchMove, { passive: true });
            header.addEventListener('touchend', handleTouchEnd, { passive: true });

            this._cleanupSwipeGestures = () => {
                header.removeEventListener('touchstart', handleTouchStart);
                header.removeEventListener('touchmove', handleTouchMove);
                header.removeEventListener('touchend', handleTouchEnd);
            };
        },
    },
    beforeDestroy() {
        gsap.killTweensOf(this.$refs.drawer);
        gsap.killTweensOf('.mobile-drawer-overlay');

        if (this._cleanupSwipeGestures) {
            this._cleanupSwipeGestures();
        }
    },
};
</script>

<style lang="scss">
.mobile-drawer-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 9999;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    will-change: opacity;
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
}

.mobile-drawer {
    width: 100%;
    max-height: v-bind(maxHeight);
    background: var(--color-white);
    border-radius: 16px 16px 0 0;
    box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.15);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    transform: translate3d(0, 100%, 0);
    will-change: transform;
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
    perspective: 1000px;
    -webkit-perspective: 1000px;
}

.mobile-drawer-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 0.5px solid var(--color-border);
    background: var(--color-white);
    position: relative;
    flex-shrink: 0;

    .mobile-drawer-handle {
        position: absolute;
        top: 8px;
        left: 50%;
        transform: translateX(-50%);
        width: 40px;
        height: 4px;
        background: var(--color-gray);
        border-radius: 2px;
        opacity: 0.6;
    }

    h3 {
        margin: 0;
        font-size: 1.1rem;
        font-weight: 600;
        color: var(--color-text-black);
        flex: 1;
        text-align: center;
    }

    .mobile-drawer-close {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        background: var(--color-blue-dark);
        color: var(--color-white);
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
        border: none;
        cursor: pointer;

        &:hover {
            background: var(--color-blue-darker);
            transform: scale(1.05);
            box-shadow: 0 2px 8px rgba(52, 152, 219, 0.3);
        }

        &:active {
            transform: scale(0.95);
        }

        i {
            font-size: 1.3rem;
            font-weight: bold;
        }
    }
}

.mobile-drawer-content {
    flex: 1;
    overflow-y: auto;
    padding: 20px 20px calc(20px + env(safe-area-inset-bottom)) 20px;
    -webkit-overflow-scrolling: touch;

    &.toolbar-variant {
        > div:not(.ToolbarActions--Group) {
            margin-bottom: 24px;
            padding-bottom: 20px;
            border-bottom: 0.5px solid var(--color-border);

            &:last-child {
                border-bottom: none;
                margin-bottom: 0;
            }

            .Dropdown,
            .Datepicker,
            .Button,
            .Checkbox {
                margin-bottom: 12px;
                width: 100%;

                &:last-child {
                    margin-bottom: 0;
                }
            }

            .Button {
                padding: 12px 16px;
                font-weight: 500;
                border-radius: 8px;
            }

            .Checkbox {
                padding: 12px 0;
                border-radius: 6px;

                &:hover {
                    background: var(--color-bg);
                }
            }

            .ToolbarActions--Group {
                margin-top: 0;
                padding-top: 0;
                * {
                    margin-bottom: 0;
                }
            }
        }
    }
}
</style>
